import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { Pencil, ArrowLeft } from 'lucide-react';
import { PersonaFormDialog } from './PersonaFormDialog';
import { IcpPersonaLinksPanel } from './IcpPersonaLinksPanel';
import type { RorosPersonaDTO } from '@/types/roros/overarching';
import {
  useRorosPersona,
  useRorosPersonaUpdate,
} from '@/hooks/roros/overarching';
import { t } from '@/lib/i18n';

export function PersonaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const personaQuery = useRorosPersona(id!);
  const updatePersona = useRorosPersonaUpdate();

  function handleSave(data: Record<string, unknown>) {
    if (!id) return;
    updatePersona.mutate(
      { id, payload: data },
      {
        onSuccess: () => {
          showToast.success(t('Persona updated'));
          setIsEditOpen(false);
        },
        onError: (err: Error) => showToast.error(err.message || t('Failed to update')),
      },
    );
  }

  return (
    <QueryPage query={personaQuery}>
      {(persona: RorosPersonaDTO) => (
        <RorosPage
          title={persona.name}
          breadcrumbs={[
            { label: t('Company Settings'), path: '/roros/company-profile' },
            { label: t('Personas'), path: '/roros/company-profile/personas' },
          ]}
          actions={
            <div className="flex gap-2">
              <Button
                variant="ghost"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
                onClick={() => navigate('/roros/company-profile?tab=market')}
              >
                {t('Back')}
              </Button>
              <Button
                variant="outline"
                leftIcon={<Pencil className="h-4 w-4" />}
                onClick={() => setIsEditOpen(true)}
              >
                {t('Edit Persona')}
              </Button>
            </div>
          }
        >
          {/* Persona details card */}
          <Card padding="md" className="mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {persona.role ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Role')}</p>
                  <p className="text-sm text-foreground">{persona.role}</p>
                </div>
              ) : null}
              {persona.decisionRole ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Decision role')}</p>
                  <p className="text-sm text-foreground capitalize">{persona.decisionRole}</p>
                </div>
              ) : null}
              {persona.influenceLevel ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Influence level')}</p>
                  <p className="text-sm text-foreground capitalize">{persona.influenceLevel}</p>
                </div>
              ) : null}
              {persona.demographics ? (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Demographics')}</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{persona.demographics}</p>
                </div>
              ) : null}
              {persona.needs ? (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Needs')}</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{persona.needs}</p>
                </div>
              ) : null}
              {persona.goals ? (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Goals')}</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{persona.goals}</p>
                </div>
              ) : null}
              {persona.painPoints ? (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Pain points')}</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{persona.painPoints}</p>
                </div>
              ) : null}
              {persona.objections ? (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Objections')}</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{persona.objections}</p>
                </div>
              ) : null}
              {persona.communicationStyle ? (
                <div className="sm:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{t('Communication style')}</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{persona.communicationStyle}</p>
                </div>
              ) : null}
              {!persona.role && !persona.decisionRole && !persona.influenceLevel && !persona.demographics && !persona.needs && !persona.goals && !persona.painPoints && !persona.objections && !persona.communicationStyle ? (
                <p className="text-sm text-muted-foreground col-span-2">{t('No details yet. Click Edit Persona to add them.')}</p>
              ) : null}
            </div>
          </Card>

          {/* Linked ICPs panel — shows reverse view */}
          {id ? <IcpPersonaLinksPanel personaId={id} /> : null}

          {/* Edit dialog — shared PersonaFormDialog (uses PersonaFormBody) */}
          <PersonaFormDialog
            isOpen={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            mode="edit"
            persona={persona}
            onSubmit={(draft) => handleSave(draft as unknown as Record<string, unknown>)}
            isSubmitting={updatePersona.isPending}
          />
        </RorosPage>
      )}
    </QueryPage>
  );
}
